import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";
import { laborAPI } from "../../api/labor";
import { useAuth } from "../../context/AuthContext"; // ← ADD THIS
import toast from "react-hot-toast";

const ClockInOut = ({ workerId, projectId, onSuccess }) => {
  const { user } = useAuth(); // ← ADD THIS
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Determine effective IDs based on role
  const effectiveWorkerId = user?.role === "worker" ? user?.id : workerId;
  const effectiveProjectId =
    user?.role === "worker" ? user?.project_id : projectId;

  // Check if worker is already clocked in (could be fetched from API)
  useEffect(() => {
    // You can fetch current status from API if needed
    // For now, we'll just use local state
  }, [effectiveWorkerId]);

  // Clock In Mutation - Always called at top level (not conditionally)
  const clockInMutation = useMutation({
    mutationFn: () =>
      laborAPI.clockIn({
        worker_id: effectiveWorkerId,
        project_id: effectiveProjectId,
      }),
    onSuccess: (data) => {
      setIsClockedIn(true);
      setLoading(false);
      toast.success("✅ Clocked in successfully");
      queryClient.invalidateQueries(["timesheets"]);
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      setLoading(false);
      toast.error(error.response?.data?.error?.message || "Failed to clock in");
    },
  });

  // Clock Out Mutation - Always called at top level (not conditionally)
  const clockOutMutation = useMutation({
    mutationFn: () =>
      laborAPI.clockOut({
        worker_id: effectiveWorkerId,
        project_id: effectiveProjectId,
      }),
    onSuccess: (data) => {
      setIsClockedIn(false);
      setLoading(false);
      toast.success("✅ Clocked out successfully");
      queryClient.invalidateQueries(["timesheets"]);
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      setLoading(false);
      toast.error(
        error.response?.data?.error?.message || "Failed to clock out",
      );
    },
  });

  const handleClockIn = () => {
    if (!effectiveWorkerId) {
      toast.error("Worker ID is required");
      return;
    }
    if (!effectiveProjectId) {
      toast.error("Please select a project first");
      return;
    }
    setLoading(true);
    clockInMutation.mutate();
  };

  const handleClockOut = () => {
    setLoading(true);
    clockOutMutation.mutate();
  };

  // If no project selected, show disabled state
  if (!effectiveProjectId) {
    return (
      <div className="flex items-center gap-2">
        <ClockIcon className="h-4 w-4 text-slate-400" />
        <span className="text-xs text-slate-400">Select a project</span>
      </div>
    );
  }

  // If user is worker and no project assigned
  if (user?.role === "worker" && !effectiveProjectId) {
    return (
      <div className="flex items-center gap-2">
        <ClockIcon className="h-4 w-4 text-slate-400" />
        <span className="text-xs text-slate-400">No project assigned</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {/* Status Indicator */}
      <div className="flex items-center gap-2">
        <ClockIcon
          className={`h-4 w-4 ${isClockedIn ? "text-green-500" : "text-slate-400"}`}
        />
        <span
          className={`text-xs font-medium ${isClockedIn ? "text-green-600 dark:text-green-400" : "text-slate-500 dark:text-slate-400"}`}
        >
          {isClockedIn ? "Active" : "Off"}
        </span>
        <span
          className={`inline-block w-2 h-2 rounded-full ${isClockedIn ? "bg-green-500 animate-pulse" : "bg-slate-300 dark:bg-slate-600"}`}
        />
      </div>

      {/* Action Buttons */}
      {isClockedIn ? (
        <button
          onClick={handleClockOut}
          disabled={loading || clockOutMutation.isLoading}
          className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <XCircleIcon className="h-3.5 w-3.5" />
          {loading || clockOutMutation.isLoading ? "..." : "Clock Out"}
        </button>
      ) : (
        <button
          onClick={handleClockIn}
          disabled={loading || clockInMutation.isLoading}
          className="px-3 py-1.5 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <CheckCircleIcon className="h-3.5 w-3.5" />
          {loading || clockInMutation.isLoading ? "..." : "Clock In"}
        </button>
      )}
    </div>
  );
};

export default ClockInOut;
